/**
 * Consent lifecycle tracking → online-service analytics.
 *
 * Hooks CookieReports' native `wscr.*` document events and a banner-visibility
 * observer, and emits consent_landing_state / consent_selection / consent_changed
 * through sendInteractionEvent(). See docs/superpowers/specs/2026-06-19-consent-lifecycle-tracking-design.md.
 */
import { parseConsentCookieStringToObject, consentObject, getCookie } from '../../common/utils/cookie-utils';
import { sendInteractionEvent } from './analytics-sender';

const LEVEL_KEYS = ['2', '3', '4', '5'];

// Map raw numeric levels to human-readable categories.
// Key 1 (strictly-necessary) is implicit and always granted; keys 2–5 are stored.
export function mapLevelsToCategories(levels) {
  const l = levels || {};
  return {
    necessary: true,
    preferences: l['2'] === true,
    statistics: l['3'] === true,
    marketing: l['4'] === true,
    category_5: l['5'] === true,
  };
}

// Pull the boolean levels object, preferring the event's numeric `consent`,
// then its `consent_string`, then a raw cookie string.
export function extractLevels(detail, cookieString) {
  if (detail && detail.consent && typeof detail.consent === 'object') {
    const out = {};
    for (const k of LEVEL_KEYS) {
      if (k in detail.consent) out[k] = detail.consent[k] === true;
    }
    if (Object.keys(out).length) return out;
  }
  const str = (detail && detail.consent_string) || cookieString || '';
  const parsed = parseConsentCookieStringToObject(str);
  const out = {};
  for (const k of LEVEL_KEYS) {
    if (k in parsed) out[k] = parsed[k] === true;
  }
  return out;
}

export function parseConsentDetail(detail, cookieString) {
  const levels = extractLevels(detail, cookieString);
  const consentString = (detail && detail.consent_string) || cookieString || '';
  return {
    levels,
    consentString,
    categories: mapLevelsToCategories(levels),
    explicit: /(^|&)consent=explicit/.test(consentString),
    trigger: (detail && detail.trigger) || '',
    reason: (detail && detail.reason) || '',
  };
}

// Decide which decision event (if any) a wscr.consent represents.
// snapshot = { hadExplicitConsent, lastSignature }
export function classifyDecision(parsed, snapshot) {
  if (parsed.trigger === 'auto') return null;            // programmatic / implied, not a user action
  const signature = parsed.consentString;
  if (signature && snapshot && signature === snapshot.lastSignature) return null; // dedupe no-op saves
  return snapshot && snapshot.hadExplicitConsent ? 'consent_changed' : 'consent_selection';
}

// Turn a wscr.consent event into {name, metaData} for the online-service layer, or null.
export function handleConsentEvent(detail, cookieString, snapshot) {
  const parsed = parseConsentDetail(detail, cookieString);
  if (Object.keys(parsed.levels).length === 0) return null;
  const name = classifyDecision(parsed, snapshot);
  if (!name) return null;
  return {
    name,
    metaData: {
      levels: parsed.levels,
      consentString: parsed.consentString,
      categories: parsed.categories,
      explicit: parsed.explicit,
      trigger: parsed.trigger,
      reason: parsed.reason,
      firstDecision: name === 'consent_selection',
    },
  };
}

// Decide the landing-state reason + displayed flag from observable signals.
export function resolveLandingState({ hadPriorConsent, bannerVisible, cmpReady }) {
  if (bannerVisible) return { reason: 'shown', displayed: true };
  if (hadPriorConsent) return { reason: 'returning_visitor', displayed: false };
  if (cmpReady) return { reason: 'not_shown', displayed: false };
  return { reason: 'cmp_unavailable', displayed: false };
}

const SESSION_LANDING_KEY = 'consentLandingReported';
const SESSION_DECISION_SIG = 'consentDecisionSignature';
const LOCAL_FIRST_DECISION = 'consentFirstDecisionMade';
const COOKIE_NAME = 'wscrCookieConsent';
const BANNER_SELECTOR = '#CookieReportsBanner, [id*="wscrBanner"], [class*="wscrBanner"]';
const CMP_SELECTOR = '[id*="wscr"], [id*="CookieReports"]';
const DISPLAY_TIMEOUT_MS = 8000;

function hasExplicitConsent(cookieObj) {
  return !!cookieObj && Object.keys(cookieObj).length > 0;
}

function isBannerVisible() {
  const el = document.querySelector(BANNER_SELECTOR);
  return !!(el && (el.offsetParent !== null || (el.getClientRects && el.getClientRects().length > 0)));
}

function isCmpReady(sawWscrEvent) {
  return !!window._cookiereports || sawWscrEvent || !!document.querySelector(CMP_SELECTOR);
}

export function initConsentTracking() {
  if (typeof document === 'undefined') return;

  let sawWscrEvent = false;
  const hadExplicitConsentAtInit = hasExplicitConsent(consentObject());

  // ---- decision events (selection / change) ----
  document.addEventListener('wscr.consent', (e) => {
    const detail = (e && e.detail) || {};
    const cookieString = getCookie(COOKIE_NAME) || '';
    const snapshot = {
      hadExplicitConsent: localStorage.getItem(LOCAL_FIRST_DECISION) === '1' || hadExplicitConsentAtInit,
      lastSignature: sessionStorage.getItem(SESSION_DECISION_SIG) || '',
    };
    const result = handleConsentEvent(detail, cookieString, snapshot);
    if (!result) return;
    sessionStorage.setItem(SESSION_DECISION_SIG, result.metaData.consentString || '');
    localStorage.setItem(LOCAL_FIRST_DECISION, '1');
    sendInteractionEvent(result.name, result.metaData);
  });

  document.addEventListener('wscr.init', () => { sawWscrEvent = true; });

  // ---- landing-state (always fires once per visit) ----
  if (sessionStorage.getItem(SESSION_LANDING_KEY) === '1') return;

  let resolved = false;
  let observer = null;
  let timer = null;

  const fireLanding = (state) => {
    if (resolved) return;
    resolved = true;
    sessionStorage.setItem(SESSION_LANDING_KEY, '1');
    if (observer) observer.disconnect();
    if (timer) clearTimeout(timer);

    const cookieString = getCookie(COOKIE_NAME) || '';
    const levels = extractLevels(null, cookieString);
    const hasLevels = Object.keys(levels).length > 0;

    sendInteractionEvent('consent_landing_state', {
      displayed: state.displayed,
      hadPriorConsent: hadExplicitConsentAtInit,
      cmpLoaded: state.reason !== 'cmp_unavailable',
      reason: state.reason,
      levels: hasLevels ? levels : null,
      consentString: hasLevels ? cookieString : null,
      categories: hasLevels ? mapLevelsToCategories(levels) : null,
    });
  };

  // Returning visitor → resolve immediately.
  if (hadExplicitConsentAtInit) {
    fireLanding(resolveLandingState({ hadPriorConsent: true, bannerVisible: false, cmpReady: true }));
    return;
  }

  // New visitor: banner may already be on screen (async CMP rendered before the bundle).
  if (isBannerVisible()) {
    fireLanding(resolveLandingState({ hadPriorConsent: false, bannerVisible: true, cmpReady: true }));
    return;
  }

  // Watch for the banner appearing.
  observer = new MutationObserver(() => {
    if (isBannerVisible()) {
      fireLanding(resolveLandingState({ hadPriorConsent: false, bannerVisible: true, cmpReady: true }));
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  // Negative case resolves on CMP readiness (primary), timeout is the backstop.
  document.addEventListener('wscr.init', () => setTimeout(() => {
    if (resolved) return;
    if (isCmpReady(true)) {
      fireLanding(resolveLandingState({ hadPriorConsent: false, bannerVisible: isBannerVisible(), cmpReady: true }));
    }
  }, 50));

  timer = setTimeout(() => {
    if (resolved) return;
    fireLanding(resolveLandingState({
      hadPriorConsent: false,
      bannerVisible: isBannerVisible(),
      cmpReady: isCmpReady(sawWscrEvent),
    }));
  }, DISPLAY_TIMEOUT_MS);
}
